import { Injectable } from '@angular/core';
import { forkJoin, map, Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { PiDataPoint, PiPoints } from '../models/pi-data.model';
import { RuleToPi } from '../models/rule.model';

export const API_NAME = 'LambdaApi';

interface DateChunk {
  from: Date;
  to: Date;
}

@Injectable({
  providedIn: 'root',
})
export class PiPointUtilityService {
  constructor(private apiService: ApiService) {}

  /**
   * Fetches PiPoints values for the given date range.
   * If the range exceeds 3 months, it splits it into 3-month chunks,
   * fires parallel API calls for each chunk, and merges the results
   * in chronological order.
   */
  public GetPisValuesChunked(
    input: RuleToPi[] | undefined,
    fromDate: any,
    toDate?: any,
    interpolationResolutionInMinutes?: any,
  ): Observable<PiPoints> {
    if (!input || input.length === 0) {
      return of({ points: [] } as PiPoints);
    }

    const resolvedToDate = toDate || new Date().toISOString();
    const resolvedFromDate =
      typeof fromDate === 'string'
        ? fromDate
        : new Date(fromDate).toISOString();

    const resolvedInterpolation = this.resolveInterpolation(
      resolvedFromDate,
      interpolationResolutionInMinutes,
    );

    const chunks = this.splitDateRange(
      new Date(resolvedFromDate),
      new Date(resolvedToDate),
    );

    if (chunks.length <= 1) {
      const requestBody = this.buildRequestBody(
        input,
        resolvedFromDate,
        resolvedToDate,
        resolvedInterpolation,
      );
      return this.apiService.post<PiPoints>(
        '/pi-points-collector',
        requestBody,
        undefined,
        false,
        API_NAME,
      );
    }

    const chunkRequests: Observable<PiPoints>[] = chunks.map((chunk) => {
      const requestBody = this.buildRequestBody(
        input,
        chunk.from.toISOString(),
        chunk.to.toISOString(),
        resolvedInterpolation,
      );
      return this.apiService.post<PiPoints>(
        '/pi-points-collector',
        requestBody,
        undefined,
        false,
        API_NAME,
      );
    });

    return forkJoin(chunkRequests).pipe(
      map((results) => this.mergePiPointsResults(results)),
    );
  }

  /**
   * Splits a date range into chunks of at most 3 calendar months.
   * If the range is <= 3 months, returns a single chunk.
   */
  splitDateRange(fromDate: Date, toDate: Date): DateChunk[] {
    const thresholdDate = new Date(fromDate);
    thresholdDate.setMonth(thresholdDate.getMonth() + 3);

    if (thresholdDate >= toDate) {
      return [{ from: new Date(fromDate), to: new Date(toDate) }];
    }

    const chunks: DateChunk[] = [];
    let currentFrom = new Date(fromDate);

    while (currentFrom < toDate) {
      const currentTo = new Date(currentFrom);
      currentTo.setMonth(currentTo.getMonth() + 3);

      if (currentTo >= toDate) {
        chunks.push({ from: new Date(currentFrom), to: new Date(toDate) });
        break;
      } else {
        chunks.push({ from: new Date(currentFrom), to: new Date(currentTo) });
      }

      currentFrom = new Date(currentTo);
    }

    return chunks;
  }

  /**
   * Merges multiple PiPoints results (one per chunk) into a single PiPoints,
   * concatenating ts/values arrays per tag in chunk order.
   */
  private mergePiPointsResults(results: PiPoints[]): PiPoints {
    if (results.length === 0) {
      return { points: [] };
    }
    if (results.length === 1) {
      return results[0];
    }

    const mergedMap = new Map<string, PiDataPoint>();

    for (const result of results) {
      for (const point of result.points) {
        const existing = mergedMap.get(point.tag);
        if (existing) {
          existing.ts = [...existing.ts, ...point.ts];
          existing.values = [...existing.values, ...point.values];
        } else {
          mergedMap.set(point.tag, {
            ...point,
            ts: [...point.ts],
            values: [...point.values],
          });
        }
      }
    }

    return { points: Array.from(mergedMap.values()) };
  }

  /**
   * Resolves interpolation resolution: uses the provided value or computes
   * a default based on how far back fromDate is (360 min if > 1 month ago, else 60 min).
   */
  private resolveInterpolation(
    fromDate: string,
    interpolationResolutionInMinutes?: number,
  ): number {
    if (interpolationResolutionInMinutes !== undefined) {
      return interpolationResolutionInMinutes;
    }
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const fromDateTime = new Date(fromDate);
    return fromDateTime < oneMonthAgo ? 360 : 60;
  }

  /**
   * Builds the request body for a single pi-points-collector API call.
   */
  private buildRequestBody(
    input: RuleToPi[],
    fromDate: string,
    toDate: string,
    interpolationResolutionInMinutes: number,
  ): { points: any[] } {
    const points = input.map((item) => ({
      tag: item.piPoint?.tag,
      server: item.piPoint?.server,
      from_date_time: fromDate,
      to_date_time: toDate,
      interpolation_resolution_in_minutes: interpolationResolutionInMinutes,
    }));
    return { points };
  }
}
